import { useCallback, useState, useEffect, useRef } from 'react';
import {
  type ProcessStepRecording,
  type StepInput,
  type StepDecisionRule,
  type WhoElseDoesThisItem,
  type EditProcessStepRecordingRequest
} from '@bliks-io/contracts';
import type { SuccessorChangeHandler } from '../types';
import { hasAnySuccessor } from '../utils/successorValidators';

interface UseProcessRecordingReturn {
  // Modal states
  showAssignInsteadModal: boolean;
  setShowAssignInsteadModal: (show: boolean) => void;
  showProcessDetailsModal: boolean;
  setShowProcessDetailsModal: (show: boolean) => void;
  showProcessFlowModal: boolean;
  setShowProcessFlowModal: (show: boolean) => void;
  showInputModal: boolean;
  setShowInputModal: (show: boolean) => void;
  showDecisionTableModal: boolean;
  setShowDecisionTableModal: (show: boolean) => void;
  editingInputIndex: number | null;
  setEditingInputIndex: (index: number | null) => void;

  // Handler functions
  handleFieldChange: (field: string, value: unknown) => Promise<void>;
  handleFieldChangeDebounced: (field: string, value: unknown) => void;
  flushPendingFieldChanges: () => Promise<void>;
  handleWhoElseDoesThisChange: (value: WhoElseDoesThisItem[]) => Promise<void>;
  handleSuccessorChange: SuccessorChangeHandler;
  handleAddInput: () => void;
  handleSaveInput: (data: StepInput) => void;
  handleEditInput: (index: number) => void;
  handleRemoveInput: (index: number) => void;
  handleSaveDecisionRules: (rules: StepDecisionRule[]) => void;
  handleOpenDecisionTable: () => void;
  handleFinishRecording: () => Promise<void>;

  // Validation functions
  hasRequiredInput: (recording: ProcessStepRecording | null) => boolean;
  hasValidTaskDescription: (recording: ProcessStepRecording | null) => boolean;
  hasValidSuccessors: (recording: ProcessStepRecording | null) => boolean;
  hasValidDecisionRules: (recording: ProcessStepRecording | null) => boolean;
  hasValidOutput: (recording: ProcessStepRecording | null) => boolean;
}

/**
 * Custom hook to manage process recording business logic
 * Extracted from ProcessStepRecording to reduce component complexity
 * Adapted for v2 to use callbacks instead of Redux
 */
export function useProcessRecording(
  currentRecording: ProcessStepRecording | null,
  onUpdateRecording: (updates: EditProcessStepRecordingRequest) => Promise<void>,
  onFinish: () => Promise<void>
): UseProcessRecordingReturn {
  // Modal states
  const [showAssignInsteadModal, setShowAssignInsteadModal] = useState(false);
  const [showProcessDetailsModal, setShowProcessDetailsModal] = useState(false);
  const [showProcessFlowModal, setShowProcessFlowModal] = useState(false);
  const [showInputModal, setShowInputModal] = useState(false);
  const [showDecisionTableModal, setShowDecisionTableModal] = useState(false);
  const [editingInputIndex, setEditingInputIndex] = useState<number | null>(null);

  // Field writes go through a pending buffer keyed by (possibly dotted) field
  // path, drained as ONE merged PATCH. A single shared debounce instance would
  // keep only the last call's args — typing description then output within the
  // debounce window used to silently drop the first field's write, leaving the
  // finish validation (which reads the server-backed cache) failing while the
  // textarea still showed the text.
  const pendingRef = useRef<Record<string, unknown>>({});
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightWritesRef = useRef(new Set<Promise<void>>());
  // Serializes flushes: a debounce timer firing while a slow write is still in
  // flight must not start a second concurrent flush — an older write that then
  // fails would re-stage a value already superseded by the newer in-flight
  // write, resurrecting stale text. Each flush snapshots the buffer only after
  // the previous one has settled.
  const flushChainRef = useRef<Promise<void>>(Promise.resolve());

  // Latest-callback refs (assigned during render): the flush/stage callbacks
  // must be identity-stable, otherwise every cache update (new
  // `currentRecording` identity) would recreate them and cancel a pending
  // timer mid-typing — the second historic lost-write path.
  const onUpdateRecordingRef = useRef(onUpdateRecording);
  onUpdateRecordingRef.current = onUpdateRecording;
  const hasRecordingRef = useRef(currentRecording !== null);
  hasRecordingRef.current = currentRecording !== null;

  const drainPendingFieldChanges = useCallback(async (): Promise<void> => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const pending = pendingRef.current;
    if (!hasRecordingRef.current || Object.keys(pending).length === 0) return;
    pendingRef.current = {};

    // Group dotted paths per parent so e.g. 'task.taskDescription' and
    // 'task.output' travel as one `{ task: { … } }` partial — the request
    // schema's nested objects are partials, so multi-key bodies are valid.
    const body: Record<string, unknown> = {};
    for (const [field, value] of Object.entries(pending)) {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        body[parent] = {
          ...(body[parent] as Record<string, unknown> | undefined),
          [child]: value
        };
      } else {
        body[field] = value;
      }
    }

    const write = onUpdateRecordingRef.current(body as EditProcessStepRecordingRequest);
    inFlightWritesRef.current.add(write);
    try {
      await write;
    } catch (error) {
      // Re-stage so the next flush retries; keystrokes typed after the failed
      // flush win over the failed values (they were merged into pendingRef
      // while this write was in flight, and serialization guarantees they are
      // still here — no concurrent flush captured them first).
      pendingRef.current = { ...pending, ...pendingRef.current };
      throw error;
    } finally {
      inFlightWritesRef.current.delete(write);
    }
  }, []);

  // Public entry point: chain each flush after the previous one settles so two
  // never overlap (see flushChainRef). A prior failure is swallowed for the
  // chain's sake but still surfaces to its own caller via the returned promise.
  const flushPendingFieldChanges = useCallback((): Promise<void> => {
    const next = flushChainRef.current.catch(() => {}).then(() => drainPendingFieldChanges());
    flushChainRef.current = next;
    return next;
  }, [drainPendingFieldChanges]);

  // Debounced staging for text inputs (500ms quiet period).
  const handleFieldChangeDebounced = useCallback(
    (field: string, value: unknown) => {
      pendingRef.current[field] = value;
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null;
        flushPendingFieldChanges().catch(() => {
          // Failed fields were re-staged; the next flush retries them.
        });
      }, 500);
    },
    [flushPendingFieldChanges]
  );

  // Immediate writes drain the buffer too, so e.g. an actionType toggle and a
  // pending description edit leave as one PATCH instead of racing each other.
  const handleFieldChange = useCallback(
    async (field: string, value: unknown) => {
      pendingRef.current[field] = value;
      await flushPendingFieldChanges();
    },
    [flushPendingFieldChanges]
  );

  // Flush (not cancel) so navigating away keeps the last keystrokes.
  useEffect(
    function flushPendingFieldsOnUnmount() {
      return () => {
        flushPendingFieldChanges().catch(() => {});
      };
    },
    [flushPendingFieldChanges]
  );

  // Who else does this handler
  const handleWhoElseDoesThisChange = useCallback(
    async (value: WhoElseDoesThisItem[]) => {
      await handleFieldChange('whoElseDoesThis', value);
    },
    [handleFieldChange]
  );

  // Successor change handler
  const handleSuccessorChange: SuccessorChangeHandler = useCallback(
    async (data) => {
      if (!currentRecording) return;

      // Merge the incoming data with existing successors
      // Successors are now string IDs directly from the edit endpoint response
      const existingSuccessors = currentRecording.task?.successors;
      const updatedSuccessors = {
        persons: data.persons ?? existingSuccessors?.persons ?? [],
        orgFunctions: data.orgFunctions ?? existingSuccessors?.orgFunctions ?? [],
        systems: data.systems ?? existingSuccessors?.systems ?? [],
        externalContacts: data.externalContacts ?? existingSuccessors?.externalContacts ?? [],
        successorStatus: data.successorStatus ?? existingSuccessors?.successorStatus ?? ''
      };

      await handleFieldChange('task.successors', updatedSuccessors);
    },
    [currentRecording, handleFieldChange]
  );

  // Unified input handlers
  const handleAddInput = useCallback(() => {
    setEditingInputIndex(null);
    setShowInputModal(true);
  }, []);

  const handleSaveInput = useCallback(
    async (data: StepInput) => {
      const currentInputs = currentRecording?.inputs || [];

      if (editingInputIndex !== null) {
        // Edit existing input
        const updatedInputs = [...currentInputs];
        updatedInputs[editingInputIndex] = data;
        await handleFieldChange('inputs', updatedInputs);
      } else {
        // Add new input
        await handleFieldChange('inputs', [...currentInputs, data]);
      }

      setShowInputModal(false);
      setEditingInputIndex(null);
    },
    [currentRecording, editingInputIndex, handleFieldChange]
  );

  const handleEditInput = useCallback((index: number) => {
    setEditingInputIndex(index);
    setShowInputModal(true);
  }, []);

  const handleRemoveInput = useCallback(
    async (index: number) => {
      const currentInputs = currentRecording?.inputs || [];
      await handleFieldChange(
        'inputs',
        currentInputs.filter((_, i) => i !== index)
      );
    },
    [currentRecording, handleFieldChange]
  );

  // Decision table handlers
  const handleSaveDecisionRules = useCallback(
    async (rules: StepDecisionRule[]) => {
      await handleFieldChange('rules', rules);
      setShowDecisionTableModal(false);
    },
    [handleFieldChange]
  );

  const handleOpenDecisionTable = useCallback(() => {
    setShowDecisionTableModal(true);
  }, []);

  // Finish recording handler: drain the buffer AND wait for writes already on
  // the wire — the stop POST must not beat a field PATCH to the server, or
  // the generated summary/validation would miss the last edit.
  const handleFinishRecording = useCallback(async () => {
    await flushPendingFieldChanges();
    await Promise.allSettled([...inFlightWritesRef.current]);
    await onFinish();
  }, [flushPendingFieldChanges, onFinish]);

  // Validation helper functions
  const hasRequiredInput = useCallback((recording: ProcessStepRecording | null): boolean => {
    if (!recording) return false;
    return recording.inputs && recording.inputs.length > 0;
  }, []);

  const hasValidTaskDescription = useCallback((recording: ProcessStepRecording | null): boolean => {
    if (!recording) return false;
    return Boolean(recording.task?.taskDescription && recording.task.taskDescription.trim() !== '');
  }, []);

  const hasValidSuccessors = useCallback((recording: ProcessStepRecording | null): boolean => {
    if (!recording || !recording.task?.successors) return false;

    const successors = recording.task.successors;

    return (
      hasAnySuccessor(successors) ||
      successors.successorStatus === 'NONE' ||
      successors.successorStatus === 'UNCLEAR'
    );
  }, []);

  const hasValidDecisionRules = useCallback((recording: ProcessStepRecording | null): boolean => {
    if (!recording || !recording.rules || recording.rules.length === 0) return false;

    // Each rule must have at least one successor assigned OR a valid end state.
    // orgFunctions / systems carry EntityOrSuggestionRef[] so pending suggestions are
    // already counted as entries in those arrays.
    return recording.rules.every((rule) => {
      if (!rule.successors) return false;

      return (
        hasAnySuccessor(rule.successors) ||
        rule.successors.successorStatus === 'NONE' ||
        rule.successors.successorStatus === 'UNCLEAR'
      );
    });
  }, []);

  const hasValidOutput = useCallback((recording: ProcessStepRecording | null): boolean => {
    if (!recording) return false;
    return Boolean(recording.task?.output && recording.task.output.trim() !== '');
  }, []);

  return {
    // Modal states
    showAssignInsteadModal,
    setShowAssignInsteadModal,
    showProcessDetailsModal,
    setShowProcessDetailsModal,
    showProcessFlowModal,
    setShowProcessFlowModal,
    showInputModal,
    setShowInputModal,
    showDecisionTableModal,
    setShowDecisionTableModal,
    editingInputIndex,
    setEditingInputIndex,

    // Handlers
    handleFieldChange,
    handleFieldChangeDebounced,
    flushPendingFieldChanges,
    handleWhoElseDoesThisChange,
    handleSuccessorChange,
    handleAddInput,
    handleSaveInput,
    handleEditInput,
    handleRemoveInput,
    handleSaveDecisionRules,
    handleOpenDecisionTable,
    handleFinishRecording,

    // Validation
    hasRequiredInput,
    hasValidTaskDescription,
    hasValidSuccessors,
    hasValidDecisionRules,
    hasValidOutput
  };
}
